import { Router, ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  ViewEncapsulation,
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { ButtonModule, LazyLoadEvent, TreeNode, Table, SelectItem } from 'primeng';
import { IPageResult } from '../../interfaces/page-result';
import { ITransactionLinesFilter } from '../../interfaces/transaction-lines-filter';
import { BillingService } from '../../services';
import { SupplierInvoiceService } from '../../services';
import { TransactionLinesService } from '../../services';
import { flatten } from '../../utils/flatten';
import { MessageService } from 'primeng';
import { Message } from '../../classes/Message';
import { JobTransactionLineRow } from '../../classes/job-transaction-line-row';

@Component({
  selector: 'app-supplier-adhoc-invoice',
  templateUrl: './supplier-adhoc-invoice.component.html',
  styleUrls: ['./supplier-adhoc-invoice.component.scss'],
  encapsulation: ViewEncapsulation.None, // todo: check why
})
export class SupplierAdhocInvoiceComponent implements OnInit {
  //public
  public filterVal: ITransactionLinesFilter;
  public filter: Subject<ITransactionLinesFilter>;
  public page: IPageResult<TreeNode> = {
    items: [],
    total: 0,
  };

  public isLoading: boolean;
  public selectedTransactions: TreeNode[];
  public cols: any[];
  public invoiceButtonDisbaled: boolean = false;
  //private
  private _manageMode: number;
  @Input('manageMode')
  set manageMode(value: number) {
    this._manageMode = value;
  }

  get manageMode(): number {
    return this._manageMode;
  }

  private _showFilterPane: boolean;
  @Input('showFilterPane')
  set showFilterPane(value: boolean) {
    this._showFilterPane = value;
  }

  get showFilterPane(): boolean {
    return this._showFilterPane;
  }

  private _invoiceSuppliers: SelectItem[];
  @Input('invoiceSuppliers')
  set invoiceSuppliers(value: SelectItem[]) {
    this._invoiceSuppliers = value;
  }

  get invoiceSuppliers(): SelectItem[] {
    return this._invoiceSuppliers.filter((s) => s.value > 0);
  }

  @Output() onHide: EventEmitter<any>;

  constructor(
    private supplierInvoiceService: SupplierInvoiceService,
    private router: Router,
    private route: ActivatedRoute,
    private transactionLinesService: TransactionLinesService,
    private billingService: BillingService,
    public messageService: MessageService,
  ) {
    this.onHide = new EventEmitter<any>();
    this.selectedTransactions = [];
    this.cols = [];
  }

  ngOnInit() {
    this.initDefaultFilter();
    this.setColums();
    if (this.showFilterPane) {
      var datesRange = this.billingService.getLastMonthRange();
      this.filterVal.filters.from = { value: datesRange.from };
      this.filterVal.filters.to = { value: datesRange.to };
    }
    this.filter = new Subject<ITransactionLinesFilter>();
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });
  }

  setColums() {
    this.cols = [
      { field: 'jobNumber', header: 'Transaction Number' },
      { field: 'lastUpdated', header: 'Transaction Completed' },
      { field: 'createdDateTime', header: 'Transaction Created' },
      { field: 'description', header: 'Description' },
      { field: 'amount', header: 'Amount' },
    ];
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: 'jobNumber',
      sortOrder: 1,
      filters: {
        processingStatus: { value: 2 },
        from: { value: null },
        to: { value: null },
        organisationId: { value: null },
      },
    };
  }

  load(filter: ITransactionLinesFilter) {
    if (this.filterVal.filters.organisationId.value > 0) {
      this.isLoading = true;
      this.resetSelections();
      this.transactionLinesService
        .getUninvoicedSupplierTransactions(flatten(filter))
        .then((result) => {
          if (result != null && result.length > 0) {
            var _result = this.getJobTransactionDataTree(result);
            this.page.items = _result.data;
            this.page.total = result.length;
          } else {
            this.messageService.add(
              Message.Success('No matching records found', 'Supplier Transactions'),
            );
          }
          this.isLoading = false;
        });
    } else {
      this.messageService.add(Message.Error('Please select a Supplier', 'Supplier Transactions'));
    }
  }

  getJobTransactionDataTree(result) {
    var _jobs = [];
    for (let res of result) {
      var data = this.getJobTransactionsLines(res);
      _jobs.push(data);
    }
    return { data: _jobs };
  }

  getJobTransactionsLines(res) {
    var amount = 0;
    for (let trans of res.transactions) {
      amount += trans.amount;
    }
    var _jobLine = res.job
      ? this.getJobDataLine(res.job, amount)
      : this.getNonMovementDataLine(res.nonMovementTransaction, amount);
    var _jobTransactions = this.getTransactionLines(res);
    var jobTransactions = { data: _jobLine, children: _jobTransactions };
    return jobTransactions;
  }

  getTransactionLines(res) {
    var transactions = [];
    for (let trans of res.transactions) {
      var data = res.job
        ? this.getTransactionDataLine(res.job, trans)
        : this.getNonMovementTransactionDataLine(res.nonMovementTransaction, trans);
      var _data = { data: data };
      transactions.push(_data);
    }
    return transactions;
  }

  getJobDataLine = (job, amount) =>
    new JobTransactionLineRow(
      job.id,
      job.jobNumber,
      job.lastUpdated,
      0,
      job.lastUpdated,
      amount,
      false,
      null,
    );
  getTransactionDataLine = (job, transaction) =>
    new JobTransactionLineRow(
      job.id,
      job.jobNumber,
      job.lastUpdated,
      transaction.id,
      transaction.createdDateTime,
      transaction.amount,
      true,
      transaction.description,
    );

  getNonMovementDataLine = (nonMovementTransaction, amount) =>
    new JobTransactionLineRow(
      nonMovementTransaction.id,
      nonMovementTransaction.transactionNumber,
      nonMovementTransaction.createdOn,
      0,
      nonMovementTransaction.createdOn,
      amount,
      false,
      null,
    );
  getNonMovementTransactionDataLine = (nonMovementTransaction, transaction) =>
    new JobTransactionLineRow(
      nonMovementTransaction.id,
      nonMovementTransaction.transactionNumber,
      nonMovementTransaction.createdOn,
      transaction.id,
      transaction.createdDateTime,
      transaction.amount,
      true,
      transaction.description,
    );

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    if (this.filterVal.filters.organisationId.value > 0) {
      this.filter.next(this.filterVal);
    }
  }

  clearFilter(dt: Table) {
    this.initDefaultFilter();
  }

  search() {
    if (this.filterVal.filters.organisationId.value > 0) {
      this.filterVal.first = 0;
      this.filter.next(this.filterVal);
    } else {
      this.messageService.add(Message.Error('Please select a Supplier', 'Supplier Transactions'));
    }
  }

  getSelectedTransactionsLines() {
    return this.selectedTransactions.filter((trans) => trans.data.isTransactionLine === true);
  }

  getSelectedTransactionsLinesTotal() {
    return this.getSelectedTransactionsLines().length;
  }

  getSelectedTransactionTotalAmount() {
    var totalAmount = 0;
    var selectedTransactions = this.getSelectedTransactionsLines();
    for (let trans of selectedTransactions) {
      if (trans.data.isTransactionLine) totalAmount += trans.data.amount;
    }
    return totalAmount;
  }

  generateInvoice() {
    //returve id's of all transactions in selected transactions
    this.invoiceButtonDisbaled = true;
    var transactionIds = [];
    var selectedTransactions = this.getSelectedTransactionsLines();
    for (let trans of selectedTransactions) {
      if (trans.data.isTransactionLine) transactionIds.push(trans.data.transactionId);
    }
    this.supplierInvoiceService
      .createInvoicesForTransactions(transactionIds)
      .then((result) => {
        this.handleGenerateInvoiceSuccessMessage(result);
        this.resetSelections();
        this.filter.next(this.filterVal);
        this.invoiceButtonDisbaled = false;
      })
      .catch((res) => {
        this.messageService.add(Message.Error(res));
        this.invoiceButtonDisbaled = false;
      });
  }

  handleGenerateInvoiceSuccessMessage(result) {
    if (typeof result == 'object' && result.billResults && result.billResults[0]) {
      if (result.billResults[0].status == 1) {
        this.messageService.add(
          Message.Success(
            `Invoice # ${result.billResults[0].invoiceNumber} Created `,
            'Generate Supplier Invoice',
          ),
        );
      } else {
        this.messageService.add(
          Message.Error(result.billResults[0].statusDetails, 'Generate Supplier Invoice'),
        );
      }
    }
  }

  resetSelections() {
    this.selectedTransactions = [];
    this.page.items = [];
    this.page.total = 0;
  }
}
